import React from "react";
import { cilInfo } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import { CAlert, CCol, CRow, CTooltip } from "@coreui/react";
import type { ApiMcModeRisk } from "@tradingdroid/frontends-types";

import { ColoredValue } from "@src/components/ColoredValue";
import { McRatingDot } from "./McRatingDot";

interface Props {
  risk: ApiMcModeRisk;
  /** The drawdown the operator said they can stomach. Drawdowns are rated against this. */
  limitPct: number;
  /** True when this mode leaves terminal P&L unchanged (reshuffle), making "ends below" a tautology. */
  terminalInvariant: boolean;
}

const InfoHint: React.FC<{ help: string }> = ({ help }) => (
  <CTooltip content={help} placement="top">
    <CIcon className="ms-1 text-info" icon={cilInfo} size="sm" style={{ cursor: "help" }} />
  </CTooltip>
);

const Stat: React.FC<{ label: string; help: string; children: React.ReactNode }> = ({
  label,
  help,
  children,
}) => (
  <CCol className="McRiskStat">
    <div className="McRiskStatLabel text-body-secondary">
      {label} <InfoHint help={help} />
    </div>
    <div className="McRiskStatValue">{children}</div>
  </CCol>
);

const sortedLevels = (levels: Record<string, number>) =>
  Object.keys(levels).sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)));

/** The numbers that decide whether a strategy survives its own bad ordering, judged against the limit. */
export const McRiskSummary: React.FC<Props> = ({ risk, limitPct, terminalInvariant }) => {
  const ruinThreshold = risk.risk_of_ruin.drawdown_threshold_pct;

  // The one planning scenario to lead with: p95 if present, else the worst level available.
  const confidenceLevels = sortedLevels(risk.drawdown_at_confidence_pct);
  const planLevel = confidenceLevels.includes("p95")
    ? "p95"
    : confidenceLevels[confidenceLevels.length - 1];
  const planDrawdown = planLevel ? risk.drawdown_at_confidence_pct[planLevel] : null;
  const overBy = planDrawdown == null ? 0 : planDrawdown - limitPct;

  return (
    <div className="McRiskSummary">
      {planDrawdown != null && (
        <CAlert color={overBy > 0 ? "danger" : "success"} className="McRiskVerdict py-2">
          At {planLevel}, the simulated max drawdown is {planDrawdown.toFixed(2)}%.{" "}
          {overBy > 0
            ? `That is ${overBy.toFixed(2)} points above your ${limitPct}% limit.`
            : `That stays within your ${limitPct}% limit.`}
        </CAlert>
      )}

      <CRow className="mb-3">
        <Stat
          label={`Chance of a ${ruinThreshold}% drawdown`}
          help={`Share of simulations whose drawdown reached your ${ruinThreshold}% limit.`}
        >
          <span className="McRiskStatValueRow">
            <McRatingDot kind="risk_of_ruin" valuePct={risk.risk_of_ruin.probability * 100} />
            <ColoredValue
              value={risk.risk_of_ruin.probability * 100}
              type="percentage"
              decimals={2}
            />
          </span>
        </Stat>
        {!terminalInvariant && (
          <Stat
            label="Ends below start"
            help="Share of simulations finishing under the initial capital."
          >
            <span className="McRiskStatValueRow">
              <McRatingDot kind="ends_below" valuePct={risk.prob_final_below_initial * 100} />
              <ColoredValue
                value={risk.prob_final_below_initial * 100}
                type="percentage"
                decimals={2}
              />
            </span>
          </Stat>
        )}
      </CRow>

      <div className="McRiskBlock mb-3">
        <div className="McRiskBlockTitle text-body-secondary">
          Drawdown at confidence
          <InfoHint help="Drawdown that the given share of simulations stayed under. Budget for this, not for the drawdown this run happened to produce." />
        </div>
        <CRow>
          {confidenceLevels.map((level) => (
            <CCol key={level} className="McRiskStat">
              <div className="McRiskStatLabel text-body-secondary">{level}</div>
              <div className="McRiskStatValue">
                <McRatingDot
                  kind="drawdown"
                  valuePct={risk.drawdown_at_confidence_pct[level]}
                  limitPct={limitPct}
                />
                <ColoredValue
                  value={risk.drawdown_at_confidence_pct[level]}
                  type="percentage"
                  decimals={2}
                />
              </div>
            </CCol>
          ))}
        </CRow>
      </div>

      <div className="McRiskBlock">
        <div className="McRiskBlockTitle text-body-secondary">
          Expected shortfall
          <InfoHint help="Average drawdown across the worst tail beyond each confidence level. The closest thing here to a usable worst case." />
        </div>
        <CRow>
          {sortedLevels(risk.expected_shortfall_max_drawdown_pct).map((level) => (
            <CCol key={level} className="McRiskStat">
              <div className="McRiskStatLabel text-body-secondary">{level}</div>
              <div className="McRiskStatValue">
                <McRatingDot
                  kind="drawdown"
                  valuePct={risk.expected_shortfall_max_drawdown_pct[level]}
                  limitPct={limitPct}
                />
                <ColoredValue
                  value={risk.expected_shortfall_max_drawdown_pct[level]}
                  type="percentage"
                  decimals={2}
                />
              </div>
            </CCol>
          ))}
        </CRow>
      </div>
    </div>
  );
};
